"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getVotingPower = getVotingPower;
exports.getProposalResults = getProposalResults;
exports.processVotingEvents = processVotingEvents;
exports.processAllProposals = processAllProposals;
const tslib_1 = require("tslib");
const client_dynamodb_1 = require("@aws-sdk/client-dynamodb");
const alphafi_sdk_1 = require("@alphafi/alphafi-sdk");
const decimal_js_1 = require("decimal.js");
const dotenv_1 = tslib_1.__importDefault(require("dotenv"));
dotenv_1.default.config();
const ENDPOINT = process.env.DYNAMODB_ENDPOINT;
const REGION = process.env.DYNAMODB_REGION;
const client = new client_dynamodb_1.DynamoDBClient({
    endpoint: ENDPOINT,
    region: REGION,
});
const VOTING_POWER_TABLE_NAME = process.env.VOTING_POWER_TABLE_NAME || "VotingPowerTable";
const VOTING_RESULTS_TABLE_NAME = process.env.VOTING_RESULTS_TABLE_NAME || "VotingResultsTable";
async function getVotingPower(timestamp, owner) {
    try {
        const params = {
            TableName: VOTING_POWER_TABLE_NAME,
            Key: {
                timestamp: { N: timestamp.toString() },
                owner: { S: owner },
            },
        };
        const response = await client.send(new client_dynamodb_1.GetItemCommand(params));
        if (response.Item &&
            response.Item.votingPower &&
            response.Item.votingPower.S) {
            return response.Item.votingPower.S;
        }
        return "0";
    }
    catch (error) {
        console.error(`Error retrieving voting power for address ${owner} at timestamp ${timestamp}:`, error);
        throw error;
    }
}
async function initializeVotingResultsTable() {
    try {
        await client.send(new client_dynamodb_1.CreateTableCommand({
            TableName: VOTING_RESULTS_TABLE_NAME,
            AttributeDefinitions: [
                { AttributeName: "proposalId", AttributeType: "S" },
            ],
            KeySchema: [{ AttributeName: "proposalId", KeyType: "HASH" }],
            BillingMode: "PAY_PER_REQUEST",
        }));
        console.log(`Table "VotingResultsTable" created successfully.`);
    }
    catch (error) {
        console.error("Error creating VotingResultsTable:", error);
        throw error;
    }
}
async function getProposalResults(proposalId) {
    try {
        const params = {
            TableName: VOTING_RESULTS_TABLE_NAME,
            Key: {
                proposalId: { S: proposalId },
            },
        };
        const response = await client.send(new client_dynamodb_1.GetItemCommand(params));
        if (!response.Item) {
            return null;
        }
        const lastUpdateTimestamp = Number(response.Item.lastUpdateTimestamp.N || "0");
        const resultsMap = response.Item.results?.M || {};
        // Convert DynamoDB map to our results array
        const results = [];
        for (const [choiceKey, valueObj] of Object.entries(resultsMap)) {
            results.push({
                voteChoice: Number(choiceKey),
                aggregatedVotes: valueObj.S || "0",
            });
        }
        return {
            lastUpdateTimestamp,
            results,
        };
    }
    catch (error) {
        console.error(`Error retrieving voting results for proposal ${proposalId}:`, error);
        throw error;
    }
}
async function updateProposalResults(proposalId, voteChoice, votingPower, isAddition, timestamp) {
    try {
        // Get current results
        const currentResults = await getProposalResults(proposalId);
        // Initialize new results if none exist
        if (!currentResults) {
            await client.send(new client_dynamodb_1.PutItemCommand({
                TableName: VOTING_RESULTS_TABLE_NAME,
                Item: {
                    proposalId: { S: proposalId },
                    lastUpdateTimestamp: { N: timestamp.toString() },
                    results: {
                        M: {
                            [voteChoice.toString()]: { S: votingPower },
                        },
                    },
                },
            }));
            return;
        }
        // Update existing results
        const resultsMap = {};
        // First, convert existing results to map
        for (const result of currentResults.results) {
            resultsMap[result.voteChoice.toString()] = { S: result.aggregatedVotes };
        }
        // Then update the specific vote choice
        const choiceKey = voteChoice.toString();
        const currentVotes = new decimal_js_1.Decimal(resultsMap[choiceKey]?.S || "0");
        const powerChange = new decimal_js_1.Decimal(votingPower);
        const newVotes = isAddition
            ? currentVotes.plus(powerChange)
            : currentVotes.minus(powerChange);
        // Only store positive values
        if (newVotes.isPositive() || newVotes.isZero()) {
            resultsMap[choiceKey] = { S: newVotes.toString() };
        }
        else {
            // If negative, set to zero
            resultsMap[choiceKey] = { S: "0" };
        }
        // Update the database
        await client.send(new client_dynamodb_1.UpdateItemCommand({
            TableName: VOTING_RESULTS_TABLE_NAME,
            Key: {
                proposalId: { S: proposalId },
            },
            UpdateExpression: "SET lastUpdateTimestamp = :timestamp, results = :results",
            ExpressionAttributeValues: {
                ":timestamp": { N: timestamp.toString() },
                ":results": { M: resultsMap },
            },
        }));
    }
    catch (error) {
        console.error("Error updating voting results:", error);
        throw error;
    }
}
async function getLastUpdateTimestamp(proposalId) {
    const lastProcessedTimestamp = (await getProposalResults(proposalId))
        ?.lastUpdateTimestamp;
    if (lastProcessedTimestamp)
        return lastProcessedTimestamp;
    else {
        const suiClient = (0, alphafi_sdk_1.getSuiClient)();
        const obj = await suiClient.getObject({
            id: proposalId,
            options: {
                showContent: true,
            },
        });
        const proposalObj = obj.data?.content;
        return proposalObj.fields.start_time;
    }
}
async function getSnapshotTimestamp(proposalId) {
    const suiClient = (0, alphafi_sdk_1.getSuiClient)();
    const obj = await suiClient.getObject({
        id: proposalId,
        options: {
            showContent: true,
        },
    });
    const proposalObj = obj.data?.content;
    return proposalObj.fields.snapshot_timestamp;
}
async function processVotingEvents(proposalId) {
    try {
        const snapshotTimestamp = await getSnapshotTimestamp(proposalId);
        const lastProcessedTimestamp = await getLastUpdateTimestamp(proposalId);
        console.log(`Processing voting events since timestamp ${lastProcessedTimestamp}...`);
        const eventType = (0, alphafi_sdk_1.getConf)().VOTE_CAST_EVENT_TYPE;
        const currentTime = Date.now();
        // Query for events
        const events = (await (0, alphafi_sdk_1.fetchEvents)({
            eventTypes: [eventType],
            startTime: lastProcessedTimestamp,
            endTime: currentTime,
        })).reverse();
        // Process each event
        for (const event of events) {
            // Only process events for our specific proposal
            if (event.proposal_id !== proposalId) {
                continue;
            }
            const voter = event.voter;
            const currentVoteChoice = event.vote_choice;
            const previousVoteChoice = event.previous_vote_choice;
            // Get voter's voting power at snapshot time
            const votingPower = await getVotingPower(snapshotTimestamp, voter);
            // If this is a vote change (not the first vote), remove voting power from previous choice
            if (previousVoteChoice !== null) {
                await updateProposalResults(proposalId, previousVoteChoice, votingPower, false, // Subtract voting power
                currentTime);
            }
            // Add voting power to current choice
            await updateProposalResults(proposalId, currentVoteChoice, votingPower, true, // Add voting power
            currentTime);
        }
        console.log(`Processed ${events.length} voting events. Latest timestamp: ${currentTime}`);
    }
    catch (error) {
        console.error("Error processing voting events:", error);
        throw error;
    }
}
async function processAllProposals() {
    const proposals = (0, alphafi_sdk_1.getConf)().ACTIVE_PROPOSAL_IDS;
    await Promise.all(proposals.map((proposal) => processVotingEvents(proposal)));
}
//# sourceMappingURL=votingResultsTable.js.map